import { useEffect, useState } from "react";
import { stats, faqs } from "@/mocks/videos";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/d9mu200bdkpk99j7ge40";

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState("");

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = (formData.get("phone_alt") as string || "").trim();

    if (honeypotValue) {
      setFormSubmitted(true);
      return;
    }

    formData.delete("phone_alt");

    try {
      const response = await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后重试";
        if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
          setFormSubmitted(true);
          return;
        }
        setFormError(serverMsg);
      }
    } catch {
      setFormError("网络错误，请稍后重试");
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于久久视频",
    description:
      "久久视频是一个泛娱乐视频内容聚合平台，聚合蜜桃视频、樱桃视频、蘑菇视频、茄子视频等泛娱乐内容，提供综艺、动漫、短视频、搞笑创意视频等免费在线观看服务。",
  };

  return (
    <>
      <SEO
        title="关于我们 - 品牌介绍与联系方式"
        description="久久视频是一个泛娱乐视频内容聚合平台，聚合蜜桃视频、樱桃视频、蘑菇视频、茄子视频等泛娱乐内容。了解久久视频品牌故事、用户口碑及联系方式。"
        keywords="久久视频,关于我们,品牌介绍,联系方式,用户口碑"
        ogType="website"
        schema={{ ...faqSchema, ...aboutSchema }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="w-full bg-background-50 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[{ label: "首页", href: "/" }, { label: "关于我们" }]}
            />

            <div className="py-8 md:py-12">
              <section className="mb-14">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-6 text-center">
                  关于久久视频
                </h1>
                <div className="text-foreground-700 leading-relaxed space-y-4 text-sm md:text-base">
                  <p>
                    <strong>久久视频</strong>是一个综合性的泛娱乐视频内容聚合平台，致力于为用户提供最优质的免费在线视频观看体验。平台聚合了<strong>蜜桃视频</strong>、<strong>樱桃视频</strong>、<strong>蘑菇视频</strong>、<strong>茄子视频</strong>等多个热门频道的优质内容，涵盖<strong>综艺节目</strong>、<strong>动漫番剧</strong>、<strong>短视频</strong>、<strong>搞笑创意视频</strong>等多种类型，让用户在一个平台上就能享受到丰富多彩的视频内容。
                  </p>
                  <p>
                    久久视频的核心理念是"轻松刷不停"——我们希望每一位用户都能在这里找到自己喜欢的视频内容，无论是想要放松心情看一段搞笑视频，还是追一部精彩的综艺节目，久久视频都能满足您的需求。
                  </p>
                  <p>
                    我们拥有一支专业的内容运营团队，每天从海量视频资源中精选最优质的内容推荐给用户。同时，久久视频也在不断优化产品体验，让视频浏览更加流畅、内容发现更加智能。
                  </p>
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8 text-center">
                  平台数据
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                  {stats.map((stat, index) => (
                    <div
                      key={index}
                      className="text-center p-6 rounded-xl bg-background-100/70 border border-background-200/60"
                    >
                      <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                        <i className={`${stat.icon} text-xl`}></i>
                      </div>
                      <div className="text-2xl md:text-3xl font-heading text-primary-500 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-sm text-foreground-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8 text-center">
                  常见问题
                </h2>
                <div className="space-y-3">
                  {faqs.map((faq, i) => (
                    <details
                      key={i}
                      className="group bg-background-100/70 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer"
                    >
                      <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-900 list-none">
                        {faq.question}
                        <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                      </summary>
                      <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-8 text-center">
                  联系我们
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-900 mb-1">
                          电子邮箱
                        </h4>
                        <p className="text-sm text-foreground-600">
                          contact@jiujiu-video.com
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-phone-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-900 mb-1">
                          客服热线
                        </h4>
                        <p className="text-sm text-foreground-600">
                          400-888-9999
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-900 mb-1">
                          公司地址
                        </h4>
                        <p className="text-sm text-foreground-600">
                          北京市朝阳区望京科技园A座
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    {formSubmitted ? (
                      <div className="bg-primary-50 border border-primary-200 rounded-xl p-8 text-center">
                        <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                          <i className="ri-check-line text-2xl"></i>
                        </div>
                        <h4 className="font-heading text-lg text-foreground-900 mb-2">
                          提交成功！
                        </h4>
                        <p className="text-sm text-foreground-600">
                          感谢您的留言，我们会尽快与您取得联系。
                        </p>
                      </div>
                    ) : (
                      <form
                        id="contact-form"
                        data-readdy-form
                        onSubmit={handleContactSubmit}
                        action={CONTACT_FORM_URL}
                        method="POST"
                        className="space-y-4"
                      >
                        <div>
                          <input
                            type="text"
                            name="name"
                            required
                            placeholder="您的姓名"
                            className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          />
                        </div>
                        <div>
                          <input
                            type="email"
                            name="email"
                            required
                            placeholder="您的邮箱"
                            className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                          />
                        </div>
                        <div>
                          <textarea
                            name="message"
                            required
                            maxLength={500}
                            rows={4}
                            placeholder="请输入您的留言（最多500字）"
                            className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                          ></textarea>
                        </div>
                        <div className="honeypot-field">
                          <input
                            type="text"
                            name="phone_alt"
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            readOnly
                          />
                        </div>
                        {formError && (
                          <p className="text-sm text-secondary-600">{formError}</p>
                        )}
                        <button
                          type="submit"
                          className="w-full px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 cursor-pointer transition-all whitespace-nowrap"
                        >
                          提交留言
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}